import React, {
    useEffect,
    useMemo,
    useState,
    useCallback,
    useRef,
    useLayoutEffect,
} from "react";
import {
    useLocation,
    useNavigate,
    useParams,
    useSearchParams,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    ArrowLeft,
    Shield,
    Search,
    Save,
    ChevronDown,
    ChevronRight,
    Info,
    Check,
    X
} from "@/components/icons/index";

import {
    getUserPermissions,
    saveUserNavigationRolePermissionProcess,
} from "@/redux/features/rolePermission/rolePermissionThunk";
import { callLoader } from "@/redux/Loader/actions";
import { clearPermissions } from "@/redux/features/rolePermission/rolePermissionSlice";
import {
    getUserDropdownList,
} from "@/redux/Masters/users/actions";
import { showToast } from "@/redux/Toast/actions";
import useAuthUser from "@/hooks/useAuthUser";
import AnimatedTitle from "../superAdmin/components/common/AnimatedTitle";

// ─── CONSTANTS ────────────────────────────────────────────────────────────────

const SAVE_STATE = { IDLE: "idle", SAVING: "saving", SAVED: "saved" };

const PERMISSION_FIELDS = [
    "CanCreate",
    "CanModify",
    "CanDelete",
    "CanView",
    "CanPrint",
    "CanMail",
];

const FIELD_LABELS = {
    CanCreate: "Create",
    CanModify: "Modify",
    CanDelete: "Delete",
    CanView: "View",
    CanPrint: "Print",
    CanMail: "Mail",
};

// ─── TREE HELPERS ─────────────────────────────────────────────────────────────

const getNodeId = (node) => {
    if (node.SubNavigationId != null) return `sub-${node.SubNavigationId}`;
    if (node.NavigationId != null) return `nav-${node.NavigationId}`;
    return `base-${node.BaseNavigationId}`;
};

const getNodeName = (node) => {
    if (node.SubNavigationId != null) return node.SubNavigationName;
    if (node.NavigationId != null) return node.NavigationName;
    return node.BaseNavigationName;
};

const getChildren = (node) => {
    if (node.SubNavigationId != null) return [];
    if (node.NavigationId != null) return node.SubNavigationDetailsList ?? [];
    return node.NavigationDetailsList ?? [];
};

const getInitialExpanded = (nodes, state = {}) => {
    nodes.forEach((node) => {
        state[getNodeId(node)] = true;
        const children = getChildren(node);
        if (children.length) getInitialExpanded(children, state);
    });
    return state;
};

// Cascade a single field down from a matched node
const setPermCascade = (nodes, nodeId, field, checked) =>
    nodes.map((node) => {
        const currentId = getNodeId(node);

        const applyToChildren = (items) =>
            items.map((child) => ({
                ...child,
                [field]: checked,
                ...(child.NavigationDetailsList && {
                    NavigationDetailsList: applyToChildren(child.NavigationDetailsList),
                }),
                ...(child.SubNavigationDetailsList && {
                    SubNavigationDetailsList: applyToChildren(
                        child.SubNavigationDetailsList,
                    ),
                }),
            }));

        if (currentId === nodeId) {
            return {
                ...node,
                [field]: checked,
                ...(node.NavigationDetailsList && {
                    NavigationDetailsList: applyToChildren(node.NavigationDetailsList),
                }),
                ...(node.SubNavigationDetailsList && {
                    SubNavigationDetailsList: applyToChildren(
                        node.SubNavigationDetailsList,
                    ),
                }),
            };
        }

        return {
            ...node,
            ...(node.NavigationDetailsList && {
                NavigationDetailsList: setPermCascade(
                    node.NavigationDetailsList,
                    nodeId,
                    field,
                    checked,
                ),
            }),
            ...(node.SubNavigationDetailsList && {
                SubNavigationDetailsList: setPermCascade(
                    node.SubNavigationDetailsList,
                    nodeId,
                    field,
                    checked,
                ),
            }),
        };
    });

// Bubble parent state up after cascade
const updateParentStates = (nodes, field) =>
    nodes.map((node) => {
        const children = getChildren(node);
        if (!children.length) return node;

        const updatedChildren = updateParentStates(children, field);
        const hasCheckedChild = updatedChildren.some((child) => child[field]);

        return {
            ...node,
            [field]: hasCheckedChild,
            ...(node.NavigationDetailsList && {
                NavigationDetailsList: updatedChildren,
            }),
            ...(node.SubNavigationDetailsList && {
                SubNavigationDetailsList: updatedChildren,
            }),
        };
    });

// Set a single field across entire tree
const setAllForField = (nodes, field, checked) =>
    nodes.map((node) => ({
        ...node,
        [field]: checked,
        ...(node.NavigationDetailsList && {
            NavigationDetailsList: setAllForField(
                node.NavigationDetailsList,
                field,
                checked,
            ),
        }),
        ...(node.SubNavigationDetailsList && {
            SubNavigationDetailsList: setAllForField(
                node.SubNavigationDetailsList,
                field,
                checked,
            ),
        }),
    }));

// Set all PERMISSION_FIELDS across entire tree
const setAllFields = (nodes, checked) =>
    nodes.map((node) => ({
        ...node,
        ...PERMISSION_FIELDS.reduce((acc, f) => ({ ...acc, [f]: checked }), {}),
        ...(node.NavigationDetailsList && {
            NavigationDetailsList: setAllFields(node.NavigationDetailsList, checked),
        }),
        ...(node.SubNavigationDetailsList && {
            SubNavigationDetailsList: setAllFields(
                node.SubNavigationDetailsList,
                checked,
            ),
        }),
    }));

// tri-state for a node + all its descendants for a single field
const getCheckState = (node, field) => {
    const children = getChildren(node);
    if (!children.length) return node[field] ? "all" : "none";

    const childStates = children.map((c) => getCheckState(c, field));
    const allChecked = node[field] && childStates.every((s) => s === "all");
    const noneChecked = !node[field] && childStates.every((s) => s === "none");

    if (allChecked) return "all";
    if (noneChecked) return "none";
    return "partial";
};

const countCheckedForField = (nodes, field) => {
    let count = 0;
    const walk = (items) =>
        items.forEach((item) => {
            if (item[field]) count++;
            walk(getChildren(item));
        });
    walk(nodes);
    return count;
};

const countAllNodes = (nodes) => {
    let count = 0;
    const walk = (items) =>
        items.forEach((item) => {
            count++;
            walk(getChildren(item));
        });
    walk(nodes);
    return count;
};

// ─── LEVEL STYLES ─────────────────────────────────────────────────────────────

const LEVEL_STYLES = {
    0: {
        row: "bg-teal-50 dark:bg-teal-950/40 border-l-[3px] border-l-teal-500",
        label: "text-sm font-semibold text-teal-900 dark:text-teal-100",
        cell: "bg-teal-50 dark:bg-teal-950/40",
        badge: "bg-teal-100 dark:bg-teal-900/60 text-teal-700 dark:text-teal-300",
        badgeLabel: "Base",
    },
    1: {
        row: "bg-blue-50 dark:bg-blue-950/30 border-l-[3px] border-l-blue-400",
        label: "text-sm font-medium text-blue-900 dark:text-blue-200",
        cell: "bg-blue-50 dark:bg-blue-950/30",
        badge: "bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300",
        badgeLabel: "Nav",
    },
    2: {
        row: "bg-amber-50/60 dark:bg-amber-950/20 border-l-[3px] border-l-amber-300",
        label: "text-xs font-normal text-amber-900 dark:text-amber-200",
        cell: "bg-amber-50/60 dark:bg-amber-950/20",
        badge:
            "bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300",
        badgeLabel: "Sub",
    },
};

// ─── TRI-CHECKBOX ─────────────────────────────────────────────────────────────

function TriCheckbox({ checkState, onChange, size = 15, className = "" }) {
    const ref = useRef(null);

    useLayoutEffect(() => {
        if (!ref.current) return;
        ref.current.indeterminate = checkState === "partial";
        ref.current.checked = checkState === "all";
    }, [checkState]);

    return (
        <input
            ref={ref}
            type="checkbox"
            className={className}
            style={{
                width: size,
                height: size,
                cursor: "pointer",
                accentColor: "#1D9E75",
            }}
            onChange={(e) => onChange(e.target.checked)}
        />
    );
}

// ─── TREE NODE ────────────────────────────────────────────────────────────────

const NAV_COL_WIDTH = 280;
const PERM_COL_WIDTH = 96;

const TreeNode = React.memo(function TreeNode({
    node,
    level,
    expandedNodes,
    toggleExpand,
    onPermissionChange,
}) {
    const children = getChildren(node);
    const hasChildren = children.length > 0;
    const nodeId = getNodeId(node);
    const isExpanded = expandedNodes[nodeId];
    const styles = LEVEL_STYLES[level] ?? LEVEL_STYLES[2];

    return (
        <>
            <div
                className={`
          grid items-center
          border-b border-slate-100 dark:border-slate-800
          brightness-[0.97] hover:brightness-100
          transition-all duration-100
          ${styles.row}
        `}
                style={{
                    gridTemplateColumns: `${NAV_COL_WIDTH}px repeat(${PERMISSION_FIELDS.length}, ${PERM_COL_WIDTH}px)`,
                }}
            >
                {/* Label cell */}
                <div
                    className={`
            sticky left-0 z-10 flex items-center gap-1.5
            py-2.5 pr-3 ${styles.cell}
            border-r border-slate-100 dark:border-slate-800
          `}
                    style={{ paddingLeft: `${level * 20 + 12}px` }}
                    onClick={() => hasChildren && toggleExpand(nodeId)}
                >
                    {hasChildren ? (
                        <ChevronRight
                            size={14}
                            className={`shrink-0 text-slate-400 transition-transform duration-150 ${isExpanded ? "rotate-90" : ""}`}
                        />
                    ) : (
                        <span className="w-3.5 shrink-0" />
                    )}

                    <span
                        className={`${styles.label} truncate ${hasChildren ? "cursor-pointer select-none" : ""}`}
                        title={getNodeName(node)}
                    >
                        {getNodeName(node)}
                    </span>

                    <span
                        className={`ml-auto shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${styles.badge}`}
                    >
                        {styles.badgeLabel}
                    </span>
                </div>

                {/* Permission checkboxes */}
                {PERMISSION_FIELDS.map((field) => (
                    <div key={field} className="flex justify-center items-center py-2.5">
                        <TriCheckbox
                            checkState={getCheckState(node, field)}
                            onChange={(checked) => onPermissionChange(nodeId, field, checked)}
                        />
                    </div>
                ))}
            </div>

            {/* Recursive children */}
            {isExpanded &&
                children.map((child) => (
                    <TreeNode
                        key={getNodeId(child)}
                        node={child}
                        level={level + 1}
                        expandedNodes={expandedNodes}
                        toggleExpand={toggleExpand}
                        onPermissionChange={onPermissionChange}
                    />
                ))}
        </>
    );
});

// ─── SELECT COMPONENT ─────────────────────────────────────────────────────────

const Select = ({ label, value, onChange, options, placeholder, disabled, onClear }) => (
    <div className="flex flex-col gap-1.5">
        <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
            {label}
        </label>

        <div className="relative">
            <select
                value={value}
                onChange={(e) => {
                    const selectedOption = options.find(
                        (opt) => String(opt.value) === e.target.value,
                    );

                    onChange(e.target.value, selectedOption);
                }}
                disabled={disabled}
                className="
          w-full appearance-none pl-3 pr-16 py-2.5
          bg-white dark:bg-slate-800
          border border-slate-200 dark:border-slate-700
          rounded-lg text-sm text-slate-900 dark:text-white
          focus:outline-none focus:ring-2 focus:ring-teal-500
          disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer
        "
            >
                <option value="">{placeholder}</option>

                {options.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                        {opt.label}
                    </option>
                ))}
            </select>

            {value && !disabled && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onClear ? onClear() : onChange("", undefined);
                    }}
                    className="absolute right-8 top-1/2 -translate-y-1/2 p-0.5 rounded-full hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                    aria-label="Clear selection"
                >
                    <X className="w-3.5 h-3.5" />
                </button>
            )}

            <ChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        </div>
    </div>
);

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

const UserPermissionPage = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const { state } = location;
    const { roleId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();

    const MasterCompanyDetails = useSelector((s) => s.common?.MasterCompanyDetails);
    const authUser = useAuthUser();
    // console.log('authUser: ', authUser);
    const masterCompany =
        (Array.isArray(MasterCompanyDetails) ? MasterCompanyDetails[0] : MasterCompanyDetails) ||
        (() => {
            try {
                const [details] = JSON.parse(localStorage.getItem("MasterCompanyDetails") ?? "[{}]");
                return details || {};
            } catch {
                return {};
            }
        })();


    const companyId =
        authUser?.companyId ??
        authUser?.CompanyId;

    const userId = searchParams.get("userId");
    const selectedUserId = userId
        ? Number(userId)
        : null;

    // ── Redux ──────────────────────────────────────────────────────────────────
    const {
        loading: permissionsLoading,
        navigationUserPermissions,
    } = useSelector((s) => s.rolePermissions);

    const { userDropdownList } = useSelector((state) => state.users);

    // ── Selection ──────────────────────────────────────────────────────────────


    // ── Tree state ─────────────────────────────────────────────────────────────
    const [treeData, setTreeData] = useState([]);
    // console.log('treeData: ', treeData);
    const [expandedNodes, setExpandedNodes] = useState({});
    const [savedState, setSavedState] = useState(SAVE_STATE.IDLE);
    const [isDirty, setIsDirty] = useState(false);

    // ── Search ─────────────────────────────────────────────────────────────────
    const [searchTerm, setSearchTerm] = useState("");


    // ── Fetch permissions ──────────────────────────────────────────────────────

    useEffect(() => {
        if (!companyId) return;

        dispatch(
            getUserDropdownList({
                CompanyId: companyId,
                RoleId: 0,
            }),
        );
    }, [dispatch, companyId]);

    useEffect(() => {
        if (!companyId || !selectedUserId || !authUser || !masterCompany) {
            return;
        }

        dispatch(callLoader(true));

        dispatch(
            getUserPermissions({
                AccountTypeId: masterCompany?.ProfileCategory,
                RoleId: authUser?.roleID,
                UserId: selectedUserId,
            })
        ).finally(() => {
            dispatch(callLoader(false));
        });
    }, [
        dispatch,
        companyId,
        selectedUserId,
        masterCompany?.ProfileCategory,
        authUser?.roleID,
    ]);

    useEffect(() => {
        dispatch(clearPermissions());
    }, [userId, dispatch]);

    // ── Sync raw → tree ────────────────────────────────────────────────────────

    useEffect(() => {
        if (navigationUserPermissions?.length) {
            setTreeData(structuredClone(navigationUserPermissions));
            setExpandedNodes(getInitialExpanded(navigationUserPermissions));
            setIsDirty(false);
        }
    }, [navigationUserPermissions]);



    // ── Derived: column header stats ──────────────────────────────────────────
    const totalNodes = useMemo(() => countAllNodes(treeData), [treeData]);

    const colStats = useMemo(
        () =>
            PERMISSION_FIELDS.map((field) => {
                const checked = countCheckedForField(treeData, field);
                const colState =
                    checked === 0 ? "none" : checked === totalNodes ? "all" : "partial";
                return { field, label: FIELD_LABELS[field], colState };
            }),
        [treeData, totalNodes],
    );

    // ── Filtered tree (search) ─────────────────────────────────────────────────
    const filterTree = useCallback((nodes, term) => {
        if (!term) return nodes;
        const lower = term.toLowerCase();

        const filterNode = (node) => {
            const name = getNodeName(node).toLowerCase();
            const children = getChildren(node);

            if (name.includes(lower)) return node;

            const filteredChildren = children.map(filterNode).filter(Boolean);
            if (!filteredChildren.length) return null;

            if (node.NavigationDetailsList != null)
                return { ...node, NavigationDetailsList: filteredChildren };
            if (node.SubNavigationDetailsList != null)
                return { ...node, SubNavigationDetailsList: filteredChildren };
            return node;
        };

        return nodes.map(filterNode).filter(Boolean);
    }, []);

    const visibleTree = useMemo(
        () => filterTree(treeData, searchTerm.trim()),
        [treeData, searchTerm, filterTree],
    );

    // console.log('visibleTree: ', visibleTree);
    // ── Handlers ──────────────────────────────────────────────────────────────
    const toggleExpand = useCallback((id) => {
        setExpandedNodes((prev) => ({ ...prev, [id]: !prev[id] }));
    }, []);

    const handlePermissionChange = useCallback((nodeId, field, checked) => {
        setTreeData((prev) => {
            const cascaded = setPermCascade(prev, nodeId, field, checked);
            return updateParentStates(cascaded, field);
        });
        setIsDirty(true);
    }, []);

    const handleColToggle = useCallback((field, checked) => {
        setTreeData((prev) => setAllForField(prev, field, checked));
        setIsDirty(true);
    }, []);

    const handleSelectAll = useCallback((checked) => {
        setTreeData((prev) => setAllFields(prev, checked));
        setIsDirty(true);
    }, []);

    // ── Save ──────────────────────────────────────────────────────────────────
    const handleSave = useCallback(async () => {
        try {
            dispatch(callLoader(true));
            setSavedState(SAVE_STATE.SAVING);

            const result = await dispatch(
                saveUserNavigationRolePermissionProcess(treeData)
            ).unwrap();

            dispatch(
                showToast(
                    result.message || "Navigation Permission saved successfully",
                    "success",
                    true,
                ),
            );

            setSavedState(SAVE_STATE.SAVED);
            setIsDirty(false);

            // Refresh should not fail the save
            try {
                await dispatch(
                    getUserPermissions({
                        AccountTypeId: masterCompany?.ProfileCategory,
                        RoleId: authUser?.roleID,
                        UserId: selectedUserId,
                    }),
                ).unwrap();

            } catch (refreshError) {
                console.error("Refresh failed:", refreshError);

                dispatch(
                    showToast(
                        refreshError.message || "Permissions saved, but failed to refresh.",
                        "warning",
                        true,
                    ),
                );
            }

            setTimeout(() => setSavedState(SAVE_STATE.IDLE), 2000);
        } catch (err) {
            console.error(err);

            dispatch(
                showToast(err.message || "Failed to save permissions.", "error", true),
            );

            setSavedState(SAVE_STATE.IDLE);
        } finally {
            dispatch(callLoader(false));
        }
    }, [
        dispatch,
        treeData,
        userId,
        selectedUserId,
    ]);
    const hasSelection = !!selectedUserId

    const userOptions = useMemo(
        () =>
            userDropdownList.map((user) => ({
                value: user.userId,
                label: user.username,
            })),
        [userDropdownList],
    );

    // ─── UI ───────────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-950 p-6">
            <div className="space-y-5">
                {/* HEADER */}
                <div className="overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
                    <div className="px-4 sm:px-5 py-4 flex flex-wrap items-center justify-between gap-3">
                        <div className="min-w-0">
                            <div className="flex items-start gap-3">
                                <button
                                    onClick={() => {
                                        if (userId) {
                                            navigate({
                                                pathname: "/admin/users",
                                                search: `?companyId=${searchParams.get("companyId") ?? ""}`,
                                            });
                                        } else {
                                            navigate("/admin/roles");
                                        }
                                    }}
                                    className="p-2 border rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                                >
                                    <ArrowLeft className="w-5 h-5" />
                                </button>
                                <Shield className="w-7 h-7 sm:w-8 sm:h-8 shrink-0 text-blue-500" strokeWidth={2.2} />

                                <AnimatedTitle title="User Permissions" />

                            </div>
                            <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm">
                                Select a user to view and manage their navigation permissions
                            </p>
                        </div>

                        <button
                            onClick={handleSave}
                            disabled={
                                !hasSelection || !isDirty || savedState === SAVE_STATE.SAVING
                            }
                            className={`
              px-4 py-2 rounded-xl flex items-center gap-2 text-sm font-medium text-white transition
              ${savedState === SAVE_STATE.SAVED
                                    ? "bg-teal-700"
                                    : "bg-teal-500 hover:bg-teal-600"
                                }
              disabled:opacity-40 disabled:cursor-not-allowed
            `}
                        >
                            {savedState === SAVE_STATE.SAVED ? (
                                <>
                                    <Check className="w-4 h-4" /> Saved
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4" /> Save{isDirty ? " *" : ""}
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* SELECTORS */}
                <div className="w-full rounded-xl border border-slate-200 bg-white p-3 sm:p-4 dark:border-slate-700 dark:bg-slate-800">
                    <div className="w-full sm:max-w-md lg:max-w-lg">
                        <Select
                            label="Select User"
                            value={selectedUserId ?? ""}
                            options={userOptions}
                            placeholder="Select User"
                            onChange={(userId) => {
                                const params = new URLSearchParams(location.search);
                                params.set("userId", String(userId));
                                navigate({ pathname: location.pathname, search: params.toString() });
                            }}
                            onClear={() => {
                                const params = new URLSearchParams(location.search);
                                params.delete("userId");
                                navigate({ pathname: location.pathname, search: params.toString() });
                            }}
                        />
                    </div>
                </div>

                {hasSelection && (
                    <>
                        {/* LEGEND + SEARCH ROW */}
                        <div className="flex flex-wrap items-center gap-3">
                            {/* Level legend */}
                            <div className="flex items-center gap-2 text-xs flex-wrap">
                                <span className="text-slate-400 font-medium">Levels:</span>
                                {[
                                    {
                                        color: "bg-teal-500",
                                        bg: "bg-teal-50 dark:bg-teal-950/50 border-teal-200 dark:border-teal-800",
                                        text: "text-teal-700 dark:text-teal-300",
                                        label: "Base",
                                    },
                                    {
                                        color: "bg-blue-400",
                                        bg: "bg-blue-50 dark:bg-blue-950/40 border-blue-200 dark:border-blue-800",
                                        text: "text-blue-700 dark:text-blue-300",
                                        label: "Navigation",
                                    },
                                    {
                                        color: "bg-amber-400",
                                        bg: "bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800",
                                        text: "text-amber-700 dark:text-amber-300",
                                        label: "Sub Navigation",
                                    },
                                ].map(({ color, bg, text, label }) => (
                                    <span
                                        key={label}
                                        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-medium ${bg} ${text}`}
                                    >
                                        <span
                                            className={`w-2 h-2 rounded-full ${color} inline-block`}
                                        />
                                        {label}
                                    </span>
                                ))}
                            </div>

                            {/* Search */}
                            <div className="relative ml-auto">
                                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder="Search navigation..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-9 pr-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 w-64"
                                />
                                {searchTerm && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setSearchTerm("");
                                        }}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-white"
                                        aria-label="Clear search"
                                    >
                                        <X className="h-4 w-4" />
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* PERMISSION TABLE */}
                        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 overflow-hidden">
                            <div className="overflow-auto max-h-[70vh] scrollbar-thin scrollbar-thumb-slate-200 dark:scrollbar-thumb-slate-700">
                                {/* Sticky header */}
                                <div
                                    className="sticky top-0 z-30 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-sm"
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: `${NAV_COL_WIDTH}px repeat(${PERMISSION_FIELDS.length}, ${PERM_COL_WIDTH}px)`,
                                    }}
                                >
                                    {/* Select-all + label */}
                                    <div className="sticky left-0 z-40 bg-white dark:bg-slate-900 px-3 py-3 border-r border-slate-100 dark:border-slate-800 flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            style={{
                                                width: 14,
                                                height: 14,
                                                accentColor: "#1D9E75",
                                                cursor: "pointer",
                                            }}
                                            checked={colStats.every((c) => c.colState === "all")}
                                            ref={(el) => {
                                                if (el)
                                                    el.indeterminate = colStats.some(
                                                        (c) =>
                                                            c.colState === "partial" ||
                                                            (c.colState === "all" &&
                                                                colStats.some((x) => x.colState !== "all")),
                                                    );
                                            }}
                                            onChange={(e) => handleSelectAll(e.target.checked)}
                                        />
                                        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                            Navigation
                                        </span>
                                    </div>

                                    {/* Per-column headers */}
                                    {colStats.map(({ field, label, colState }) => (
                                        <div
                                            key={field}
                                            className="flex flex-col items-center justify-center gap-2 py-3 px-1 hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors"
                                        >
                                            <TriCheckbox
                                                checkState={colState}
                                                size={14}
                                                onChange={(checked) => handleColToggle(field, checked)}
                                            />
                                            <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 text-center">
                                                {label}
                                            </span>
                                        </div>
                                    ))}
                                </div>

                                {/* Tree body */}
                                {permissionsLoading ? (
                                    <div className="flex items-center justify-center py-20">
                                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-500" />
                                    </div>
                                ) : visibleTree.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center py-20 gap-3 text-slate-400">
                                        <Shield className="w-10 h-10 opacity-30" />
                                        <p className="text-sm">
                                            {searchTerm
                                                ? "No matching navigation items"
                                                : "No permissions found"}
                                        </p>
                                    </div>
                                ) : (
                                    visibleTree?.map((node) => (
                                        <TreeNode
                                            key={getNodeId(node)}
                                            node={node}
                                            level={0}
                                            expandedNodes={expandedNodes}
                                            toggleExpand={toggleExpand}
                                            onPermissionChange={handlePermissionChange}
                                        />
                                    ))
                                )}
                            </div>

                            {/* Footer */}
                            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-800/30">
                                <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                                    <Info className="w-3 h-3" />
                                    <span>Toggling a parent updates all its children</span>
                                </div>
                                <button
                                    onClick={handleSave}
                                    disabled={!isDirty || savedState === SAVE_STATE.SAVING}
                                    className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white bg-teal-500 hover:bg-teal-600 active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                                >
                                    <Save className="w-3.5 h-3.5" />
                                    {userId ? `Save User Permissions` : `Save Permissions`}
                                </button>
                            </div>
                        </div>
                    </>
                )}

                {/* EMPTY PROMPT */}
                {!hasSelection && (
                    <div className="flex flex-col items-center justify-center min-h-[420px] p-8 bg-gradient-to-br from-slate-50 via-white to-violet-50/50 dark:from-slate-900 dark:via-slate-800 dark:to-violet-950/20 border border-slate-200 dark:border-slate-700 rounded-xl">
                        <div className="flex items-center justify-center w-16 h-16 mb-5 rounded-2xl bg-violet-100 dark:bg-violet-900/30">
                            <Shield className="w-8 h-8 text-violet-500 dark:text-violet-400" />
                        </div>

                        <h3 className="text-base font-semibold text-slate-700 dark:text-slate-200">
                            No User Selected
                        </h3>

                        <p className="max-w-sm mt-2 text-sm text-center text-slate-500 dark:text-slate-400">
                            Select a user from the dropdown above to view and manage their navigation permissions.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserPermissionPage;
